/**
 * pages/LabResultsPage.tsx — CLI-451
 * Результаты лаборатории пациента. Read-only, с AI-флагами.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getLabOrders, type LabOrder } from '../services/patient.service';

function flagBadge(flag: string) {
  const map: Record<string, string> = {
    NORMAL: 'bg-green-100 text-green-700',
    ATTENTION: 'bg-amber-100 text-amber-700',
    CRITICAL: 'bg-red-100 text-red-700',
    EMPTY: 'bg-gray-100 text-gray-500',
  };
  const labels: Record<string, string> = {
    NORMAL: 'Норма', ATTENTION: 'Внимание', CRITICAL: 'Критично', EMPTY: '—',
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${map[flag] ?? 'bg-gray-100 text-gray-500'}`}>
      {labels[flag] ?? flag}
    </span>
  );
}

function statusLabel(status: string): string {
  const m: Record<string, string> = {
    NEW: 'Новый', IN_PROGRESS: 'В работе', COMPLETED: 'Готов', SENT: 'Отправлен', CANCELLED: 'Отменён',
  };
  return m[status] ?? status;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

function OrderCard({ order }: { order: LabOrder }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-4 flex items-center justify-between gap-2"
      >
        <div>
          <p className="font-semibold text-gray-800 text-sm">Заказ №{order.orderNumber}</p>
          <p className="text-xs text-gray-500">{formatDate(order.completedAt ?? order.createdAt)} · {statusLabel(order.status)}</p>
        </div>
        <span className="text-gray-400 text-lg">{open ? '▲' : '▼'}</span>
      </button>
      {open && order.results.length > 0 && (
        <div className="border-t border-gray-100 divide-y divide-gray-50">
          {order.results.map((r) => (
            <div key={r.id} className="px-4 py-3 flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-800 font-medium truncate">{r.test.name}</p>
                <p className="text-xs text-gray-500">
                  {r.value ?? '—'} {r.unit ?? r.test.unit ?? ''}
                  {r.referenceMin != null && r.referenceMax != null && (
                    <span className="ml-1">(норма: {r.referenceMin}–{r.referenceMax})</span>
                  )}
                </p>
              </div>
              {flagBadge(r.aiFlag)}
            </div>
          ))}
        </div>
      )}
      {open && order.results.length === 0 && (
        <p className="px-4 pb-3 text-sm text-gray-400">Результаты ещё не добавлены</p>
      )}
    </div>
  );
}

export function LabResultsPage() {
  const { data: orders, isLoading, isError } = useQuery({
    queryKey: ['patient-lab'],
    queryFn: getLabOrders,
  });

  if (isLoading) return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="bg-white rounded-xl h-16 animate-pulse" />
      ))}
    </div>
  );

  if (isError) return (
    <div className="p-4 text-center text-red-500">Ошибка загрузки анализов.</div>
  );

  if (!orders?.length) return (
    <div className="p-4 text-center text-gray-500 mt-12">
      <p className="text-4xl mb-3">🧪</p>
      <p className="font-medium text-gray-700">Результатов анализов пока нет</p>
    </div>
  );

  return (
    <div className="p-4 space-y-3">
      <h2 className="text-lg font-bold text-gray-800">Мои анализы</h2>
      {orders.map((order) => <OrderCard key={order.id} order={order} />)}
    </div>
  );
}
